import type { EChartsOption } from 'echarts-for-react/lib/types';
import {
  buildStageDeviceConfig,
  buildCustomChartConfig,
  buildStandardChartConfig,
  buildWeatherChartConfig,
} from 'components/Charts/chartConfigs/chartConfigStrategies';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue | undefined };

type ChartDataPointObject = {
  x?: string | number;
  y?: number;
  value?: number;
  [key: string]: JsonValue | undefined;
};

type ChartDataPoint = number | string | ChartDataPointObject | null | undefined;

type ChartDataset = {
  label?: string;
  type?: 'line' | 'bar';
  data?: ChartDataPoint[];
  _meta?: Record<string, JsonValue>;
  showLine?: boolean;
  tension?: number;
  showSymbol?: boolean;
  pointRadius?: number;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderDash?: number[];
  fill?: boolean;
  stack?: string;
};

type ChartJsLikeDataSets = {
  labels?: Array<string | number>;
  datasets?: ChartDataset[];
};

type ChartOptions = {
  scales?: {
    x?: {
      labels?: Array<string | number>;
    };
  };
  plugins?: {
    verticalLinePlugin?: { enabled?: boolean };
    weatherIconAtPoint?: { enabled?: boolean };
    windDirectionIcon?: { enabled?: boolean };
  };
  chartConfigType?: string;
  [key: string]: JsonValue | undefined;
};

type CreateChartOptionFromDataSetsArgs = {
  type?: 'line' | 'bar';
  dataSets?: ChartJsLikeDataSets;
  options?: ChartOptions;
};

type AdaptedSeries = {
  name?: string;
  type: 'line' | 'bar';
  data: Array<number | string | null | undefined>;
  _meta?: Record<string, JsonValue>;
  _rawData: ChartDataPoint[];
  smooth: boolean;
  showSymbol: boolean;
  symbolSize: number;
  itemStyle: {
    color?: string;
    borderColor?: string;
    borderWidth: number;
  };
  lineStyle: {
    color?: string;
    width: number;
    type: 'dashed' | 'solid';
  };
  areaStyle?: {
    color?: string;
    opacity: number;
  };
  yAxisIndex: number;
  stack?: string;
};

type ChartContext = {
  xAxisData: Array<string | number>;
  series: AdaptedSeries[];
  dataSets: ChartJsLikeDataSets;
  options: ChartOptions;
  type: 'line' | 'bar';
};

const extractValue = (value: ChartDataPoint): number | string | null | undefined => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // API metric fields (e.g. 15-day forecast) arrive as numeric strings
    const numericValue = parseFloat(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }
  if (value.value !== undefined) return value.value as number;
  if (value.y !== undefined) return value.y;
  return undefined;
};

/**
 * Convert chart.js-like datasets into an ECharts option object.
 *
 * @param args Chart type, data sets, and chart options.
 * @returns ECharts option or null when no plottable data exists.
 */
export const createChartOptionFromDataSets = ({
  type = 'line',
  dataSets = {},
  options = {},
}: CreateChartOptionFromDataSetsArgs): EChartsOption | null => {
  const datasets = dataSets?.datasets || [];
  if (datasets.length === 0) return null;

  let xAxisData = dataSets?.labels || options?.scales?.x?.labels || [];

  const series = datasets.map((dataset) => {
    const firstDataPoint = dataset.data?.[0];
    const hasXValues =
      firstDataPoint && typeof firstDataPoint === 'object' && firstDataPoint.x !== undefined;
    const rawData = dataset.data || [];

    if (hasXValues && xAxisData.length === 0) {
      xAxisData = rawData.map((point) =>
        typeof point === 'object' && point !== null && point.x !== undefined ? point.x : '',
      );
    }

    const chartType = dataset.type || type;
    const showLine = dataset.showLine !== false;

    return {
      name: dataset.label,
      type: (chartType === 'line' ? 'line' : 'bar') as 'line' | 'bar',
      data: rawData.map(extractValue),
      _meta: dataset._meta,
      _rawData: rawData,
      smooth: (dataset.tension || 0) > 0,
      showSymbol: dataset.showSymbol !== false,
      symbolSize: dataset.pointRadius || 6,
      itemStyle: {
        color: dataset.backgroundColor || dataset.borderColor,
        borderColor: dataset.borderColor,
        borderWidth: dataset.borderWidth || 0,
      },
      lineStyle: {
        color: dataset.borderColor,
        width: showLine ? dataset.borderWidth || 2 : 0,
        type: (dataset.borderDash?.length && dataset.borderDash.length > 0
          ? 'dashed'
          : 'solid') as 'dashed' | 'solid',
      },
      areaStyle: dataset.fill
        ? {
            color: dataset.backgroundColor,
            opacity: 0.3,
          }
        : undefined,
      yAxisIndex: 0,
      stack: dataset.stack,
    };
  });

  if (xAxisData.length === 0 && series[0]?.data && Array.isArray(series[0].data)) {
    xAxisData = series[0].data.map((_, index) => index.toString());
  }

  if (xAxisData.length === 0) return null;

  const context: ChartContext = { xAxisData, series, dataSets, options, type };

  if (options?.plugins?.verticalLinePlugin?.enabled) {
    return buildStageDeviceConfig(context) as EChartsOption;
  }
  if (
    options?.plugins?.weatherIconAtPoint?.enabled ||
    options?.plugins?.windDirectionIcon?.enabled
  ) {
    return buildWeatherChartConfig(context) as EChartsOption;
  }
  if (options?.chartConfigType) {
    return buildCustomChartConfig(context, options.chartConfigType) as EChartsOption;
  }
  return buildStandardChartConfig(context) as EChartsOption;
};